import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Typography,
  Modal,
  Box,
  TextField,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime"; // Clock Icon
import CategoryIcon from "@mui/icons-material/Category"; // Material Icon
import debounce from "lodash.debounce";
import { getToken, base_url } from "../../config/config";

const FinalCalculations = () => {
  // -----------------------------
  // State Variables
  // -----------------------------

  // Main FinalCalculations data
  const [data, setData] = useState([]);

  // States for "Add Time" modal
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [openAddTime, setOpenAddTime] = useState(false);
  const [searchANRAddTime, setSearchANRAddTime] = useState("");
  const [currentFinalCalcIdForTime, setCurrentFinalCalcIdForTime] =
    useState(null);

  // States for "Add New Material" modal
  const [searchTermAddNewModel, setSearchTermAddNewModel] = useState("");
  const [searchResultsAddNewModel, setSearchResultsAddNewModel] = useState([]);
  const [selectedRechnung, setSelectedRechnung] = useState(null);
  const [openAddNewModel, setOpenAddNewModel] = useState(false);
  const [loadingAddNewModel, setLoadingAddNewModel] = useState(false);

  // Flattened items with Rechnung ID
  const [flattenedItems, setFlattenedItems] = useState([]);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'error', 'warning', 'info', 'success'
  });

  // States for "View Time" modal
  const [openViewTime, setOpenViewTime] = useState(false);
  const [currentTimeData, setCurrentTimeData] = useState([]);

  // State for Edit Modal
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editData, setEditData] = useState({
    id: "",
    Modified_ANR: "",
    Modified_Menge: 0,
    Modified_Einzelpreis: "",
    Modified_TotalTime: "", // New Field
  });

  // State for Selected Rows
  const [selectedRowData, setSelectedRowData] = useState({});

  // State for Calculation Results
  const [calculationResult, setCalculationResult] = useState(null);
  const [openCalculationModal, setOpenCalculationModal] = useState(false);

  // -----------------------------
  // New State Variables for Percentage Modal
  // -----------------------------
  const [openPercentageModal, setOpenPercentageModal] = useState(false);
  const [searchANRPercentage, setSearchANRPercentage] = useState("");
  const [percentageData, setPercentageData] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [loadingPercentage, setLoadingPercentage] = useState(false);

  // -----------------------------
  // Data Fetching Functions
  // -----------------------------

  // Fetch main FinalCalculations data
  const fetchFinalCalculations = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getFinalreport`);
      console.log("Fetched Finalreport Data:", response.data);

      const modifiedData = response.data.map((item) => {
        const {
          _id,
          excelBulkId,
          materials,
          combinedTotalTime,
          totalUserCost,
          totalStuckCostSum,
          finalProductCost,
          Modified_ANR,
          Modified_Menge,
          Modified_Einzelpreis,
          Modified_TotalTime,
        } = item;

        // Safely parse numerical fields
        const finalMenge =
          Modified_Menge !== undefined &&
          Modified_Menge !== null &&
          !isNaN(Modified_Menge)
            ? parseFloat(Modified_Menge)
            : excelBulkId.Menge !== undefined &&
              excelBulkId.Menge !== null &&
              !isNaN(excelBulkId.Menge)
            ? parseFloat(excelBulkId.Menge)
            : 0;

        const finalEinzelpreis = Modified_Einzelpreis
          ? parseFloat(Modified_Einzelpreis)
          : excelBulkId.Einzelpreis !== undefined &&
            excelBulkId.Einzelpreis !== null &&
            !isNaN(excelBulkId.Einzelpreis)
          ? parseFloat(excelBulkId.Einzelpreis)
          : 0;

        // Correctly access Gesamtpreis from excelBulkId with proper casing
        const gesamtpreis = excelBulkId.Gesamtpreis; // Ensure the key matches exactly

        const totalPrice =
          gesamtpreis !== undefined && !isNaN(gesamtpreis)
            ? parseFloat(gesamtpreis)
            : 0;

        const safeFinalProductCost =
          finalProductCost !== undefined &&
          finalProductCost !== null &&
          !isNaN(finalProductCost)
            ? finalProductCost
            : 0;

        const profitCost = totalPrice - safeFinalProductCost;
        const profitPercentage =
          totalPrice !== 0 && !isNaN(profitCost)
            ? (profitCost / totalPrice) * 100
            : 0;

        return {
          mainId: _id,

          ANR: Modified_ANR || excelBulkId.ANR,
          Einzelpreis: Modified_Einzelpreis || excelBulkId.Einzelpreis,
          Menge: finalMenge,

          invoiceNo: excelBulkId.invoiceNo,
          totalPrice: !isNaN(totalPrice) ? totalPrice : 0, // Now correctly mapped
          profitCost: !isNaN(profitCost) ? profitCost : 0,
          profitPercentage: !isNaN(profitPercentage) ? profitPercentage : 0,
          materials: materials || [],
          combinedTotalTime,
          totalUserCost: !isNaN(totalUserCost) ? totalUserCost : 0,
          totalStuckCostSum: !isNaN(totalStuckCostSum) ? totalStuckCostSum : 0,
          finalProductCost: safeFinalProductCost,

          Modified_ANR,
          Modified_Menge,
          Modified_Einzelpreis,
          Modified_TotalTime,
        };
      });

      console.log(
        "Modified Data with Combined Time, Cost, and Profit:",
        modifiedData,
      );

      setData(modifiedData);
    } catch (error) {
      console.error("Error fetching the report data", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch FinalCalculations data.",
        severity: "error",
      });
    }
  };

  // Fetch all Rechnungs data from getallRechnungs and flatten items
  const fetchAllRechnungs = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getallRechnungs`);
      console.log("Fetched allRechnungs Data:", response.data);

      // Flatten items with their corresponding Rechnung ID
      const allItems = response.data.flatMap((rechnung) => {
        if (Array.isArray(rechnung.items)) {
          return rechnung.items.map((item) => ({
            ...item,
            rechnungId: rechnung.id || rechnung._id, // Adjust based on your schema
          }));
        }
        return [];
      });

      console.log("Flattened Items:", allItems);

      setFlattenedItems(allItems);
    } catch (error) {
      console.error("Error fetching allRechnungs data", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch all Rechnungs data.",
        severity: "error",
      });
    }
  };

  // Fetch projects data for "Add Time" modal
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getAllUserProjects`);
      setProjects(response.data.data); // Store all projects
    } catch (error) {
      console.error("Error fetching the project data", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch projects data.",
        severity: "error",
      });
    }
  };

  // -----------------------------
  // useEffect Hook
  // -----------------------------

  useEffect(() => {
    fetchFinalCalculations();
    fetchAllRechnungs();
    fetchProjects();
  }, []);

  // -----------------------------
  // Handler Functions
  // -----------------------------

  // Handle ANR search change in "Add Time" modal
  const handleSearchChangeAddTime = (event) => {
    const value = event.target.value;
    setSearchANRAddTime(value);

    // Filter projects based on user input (search by ANR or any other field)
    if (value.length >= 3) {
      const filtered = projects.filter(
        (project) => project.ANR.toLowerCase().includes(value.toLowerCase()), // Filter by ANR
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects([]);
    }
  };

  // Open "Add Time" modal
  const handleOpenAddTimeModal = (finalCalcId, anr) => {
    setCurrentFinalCalcIdForTime(finalCalcId);
    setSearchANRAddTime(anr || ""); // Pre-populate with ANR if available
    setOpenAddTime(true);
  };

  // Close "Add Time" modal
  const handleCloseAddTimeModal = () => {
    setOpenAddTime(false);
    setSearchANRAddTime("");
    setFilteredProjects([]);
    setCurrentFinalCalcIdForTime(null);
  };

  // Open "Add New Material" modal with FinalCalculations ID
  const handleAddNewModelModalWithId = (finalCalcId, anr) => {
    setSelectedRechnung({ finalCalculationId: finalCalcId }); // Store the FinalCalculations ID
    setSearchTermAddNewModel(anr || ""); // Pre-populate with ANR if available
    setOpenAddNewModel(true);
  };

  // Close "Add New Material" modal
  const handleCloseAddNewModelModal = () => {
    setOpenAddNewModel(false);
    setSearchTermAddNewModel("");
    setSearchResultsAddNewModel([]);
    setSelectedRechnung(null);
    setLoadingAddNewModel(false);
  };

  // Debounced search function for items in "Add New Material" modal
  const debouncedSearchItems = useCallback(
    debounce((searchTerm) => {
      searchItems(searchTerm);
    }, 500),
    [flattenedItems],
  );

  // Handle ANR search change in "Add New Material" modal
  const handleSearchChangeAddNewModel = (event) => {
    const value = event.target.value;
    setSearchTermAddNewModel(value);

    if (value.length >= 3) {
      debouncedSearchItems(value);
    } else {
      setSearchResultsAddNewModel([]);
    }
  };

  // Inside FinalCalculations.js
  useEffect(() => {
    if (searchANRAddTime.length >= 3) {
      const filtered = projects.filter((project) =>
        project.ANR.toLowerCase().includes(searchANRAddTime.toLowerCase()),
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects([]);
    }
  }, [searchANRAddTime, projects]);

  useEffect(() => {
    if (searchTermAddNewModel.length >= 3) {
      debouncedSearchItems(searchTermAddNewModel);
    } else {
      setSearchResultsAddNewModel([]);
    }
  }, [searchTermAddNewModel, debouncedSearchItems]);

  // Search items based on ANR in "Add New Material" modal
  const searchItems = (searchTerm) => {
    setLoadingAddNewModel(true);
    // Filter flattenedItems based on ANR
    const filteredItems = flattenedItems.filter((item) =>
      item.ANR.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    console.log("Filtered Items:", filteredItems);

    setSearchResultsAddNewModel(filteredItems);
    setLoadingAddNewModel(false);
  };

  // Handle selecting a Rechnung from search results
  const handleSelectRechnung = (item) => {
    // Find the Rechnung object based on Rechnung ID
    axios
      .get(`${base_url}/v1/getallRechnungs/${item.rechnungId}`)
      .then((response) => {
        console.log("Selected Rechnung Details:", response.data);
        setSelectedRechnung((prev) => ({
          ...prev,
          RechnungDetails: response.data, // Store Rechnung details
        }));
      })
      .catch((error) => {
        console.error("Error fetching selected Rechnung:", error);
        setSnackbar({
          open: true,
          message: "Failed to fetch selected Rechnung details.",
          severity: "error",
        });
      });
  };

  // Handle adding material directly from items table in modal
  const handleAddMaterial = async (material) => {
    // Ensure that material is an object with necessary fields
    if (
      !material ||
      !material.ANR ||
      !material.rechnungId ||
      material.singleStuckCost == null ||
      material.numberOfStuck == null ||
      material.totalStuckCost == null
    ) {
      setSnackbar({
        open: true,
        message: "Incomplete material data.",
        severity: "error",
      });
      return;
    }

    if (!selectedRechnung || !selectedRechnung.finalCalculationId) {
      setSnackbar({
        open: true,
        message: "No FinalCalculations entry selected.",
        severity: "error",
      });
      return;
    }

    try {
      // Send the full material details in the payload
      const response = await axios.post(
        `${base_url}/v1/addMaterialtoFinalCost/${selectedRechnung.finalCalculationId}`,
        {
          ANR: material.ANR,
          rechnungId: material.rechnungId,
          singleStuckCost: material.singleStuckCost,
          numberOfStuck: material.numberOfStuck,
          totalStuckCost: material.totalStuckCost,
        },
      );
      console.log("Add Material Response:", response.data);
      setSnackbar({
        open: true,
        message: "Material added successfully.",
        severity: "success",
      });
      // Refresh data or update state here
      fetchFinalCalculations();
      handleCloseAddNewModelModal();
    } catch (error) {
      console.error("Error adding material:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to add material.",
        severity: "error",
      });
    }
  };

  // Handle adding user directly from table row in "Add Time" modal
  const handleAddUser = async (userId) => {
    if (!currentFinalCalcIdForTime) {
      setSnackbar({
        open: true,
        message: "No FinalCalculations entry selected.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await axios.post(
        `${base_url}/v1/addUsertoFinalCost/${currentFinalCalcIdForTime}`,
        { userId },
      );
      console.log("Add User Response:", response.data);
      setSnackbar({
        open: true,
        message: "User added successfully.",
        severity: "success",
      });
      fetchFinalCalculations();
      handleCloseAddTimeModal();
    } catch (error) {
      console.error("Error adding user:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to add user.",
        severity: "error",
      });
    }
  };

  // Handler for viewing time data
  const handleViewTime = async (finalCalcId) => {
    try {
      // Replace with your actual endpoint for fetching time data
      const response = await axios.get(
        `${base_url}/v1/getTimeData/${finalCalcId}`,
      );
      console.log("Fetched Time Data:", response.data);
      setCurrentTimeData(response.data.timeData); // Adjust based on your API response structure
      setOpenViewTime(true);
    } catch (error) {
      console.error("Error fetching time data:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch time data.",
        severity: "error",
      });
    }
  };

  // Handle closing the "View Time" modal
  const handleCloseViewTime = () => {
    setOpenViewTime(false);
    setCurrentTimeData([]);
  };

  // Handle viewing materials (existing functionality)
  const [openViewMaterials, setOpenViewMaterials] = useState(false);
  const [currentMaterials, setCurrentMaterials] = useState([]);

  const handleViewMaterials = (materials) => {
    setCurrentMaterials(materials);
    setOpenViewMaterials(true);
  };

  const handleCloseViewMaterials = () => {
    setOpenViewMaterials(false);
    setCurrentMaterials([]);
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // -----------------------------
  // Edit Modal Handler Functions
  // -----------------------------

  // Handle opening the Edit Modal with current row data
  const handleEditRow = (rowData) => {
    setEditData({
      id: rowData.mainId,
      Modified_ANR: rowData.Modified_ANR || "",
      Modified_Menge:
        rowData.Modified_Menge !== undefined && rowData.Modified_Menge !== null
          ? rowData.Modified_Menge
          : 0,
      Modified_Einzelpreis: rowData.Modified_Einzelpreis || "",
      Modified_TotalTime: rowData.Modified_TotalTime || "", // Initialize with existing value or empty
    });
    setOpenEditModal(true);
  };

  // Handle closing the Edit Modal
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditData({
      id: "",
      Modified_ANR: "",
      Modified_Menge: 0,
      Modified_Einzelpreis: "",
      Modified_TotalTime: "",
    });
  };

  // Handle input changes in the Edit Modal
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle submitting the Edit Modal
  const handleSubmitEdit = async () => {
    const {
      id,
      Modified_ANR,
      Modified_Menge,
      Modified_Einzelpreis,
      Modified_TotalTime,
    } = editData;

    // Input validation
    if (
      (Modified_ANR && Modified_ANR.trim().length < 3) ||
      (Modified_Einzelpreis && isNaN(parseFloat(Modified_Einzelpreis))) ||
      Modified_Menge < 0 ||
      (Modified_TotalTime && isNaN(parseFloat(Modified_TotalTime)))
    ) {
      setSnackbar({
        open: true,
        message: "Please provide valid inputs.",
        severity: "warning",
      });
      return;
    }

    try {
      const payload = {};
      if (Modified_ANR.trim() !== "")
        payload.Modified_ANR = Modified_ANR.trim();
      if (Modified_Menge !== undefined && Modified_Menge !== null)
        payload.Modified_Menge = Modified_Menge;
      if (Modified_Einzelpreis.trim() !== "")
        payload.Modified_Einzelpreis = Modified_Einzelpreis.trim();
      if (
        Modified_TotalTime !== undefined &&
        Modified_TotalTime !== null &&
        Modified_TotalTime !== ""
      ) {
        payload.Modified_TotalTime = parseFloat(Modified_TotalTime);
      }

      const response = await axios.put(
        `${base_url}/v1/updateFinalCalculation/${id}`,
        payload,
      );
      console.log("Update Response:", response.data);
      setSnackbar({
        open: true,
        message: "FinalCalculations updated successfully.",
        severity: "success",
      });
      fetchFinalCalculations(); // Refresh data
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating FinalCalculations:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.error || "Failed to update FinalCalculations.",
        severity: "error",
      });
    }
  };

  // -----------------------------
  // Calculation Handler Functions
  // -----------------------------

  // Handle opening the Calculation Modal
  const handleOpenCalculationModal = () => {
    const selectedIds = Object.keys(selectedRowData).filter(
      (id) => selectedRowData[id],
    );
    console.log("Selected Row IDs:", selectedIds);

    if (selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row to calculate profit/loss.",
        severity: "warning",
      });
      return;
    }

    // Get selected rows data
    const selectedRows = data.filter((row) => selectedIds.includes(row.mainId));
    console.log("Selected Rows Data:", selectedRows);

    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Selected rows do not contain valid data for calculation.",
        severity: "error",
      });
      return;
    }

    // Calculate combined profitCost and totalPrice
    const combinedProfitCost = selectedRows.reduce(
      (acc, row) => acc + row.profitCost,
      0,
    );
    const combinedTotalPrice = selectedRows.reduce(
      (acc, row) => acc + row.totalPrice,
      0,
    );

    // Calculate combined Profit Percentage
    let combinedProfitPercentage = 0;
    if (combinedTotalPrice !== 0) {
      combinedProfitPercentage =
        (combinedProfitCost / combinedTotalPrice) * 100;
    }

    setCalculationResult({
      combinedProfitCost,
      combinedProfitPercentage,
    });

    setOpenCalculationModal(true);
  };

  // Handle closing the Calculation Modal
  const handleCloseCalculationModal = () => {
    setOpenCalculationModal(false);
    setCalculationResult(null);
  };

  // -----------------------------
  // New Handler Functions for Percentage Modal
  // -----------------------------

  // Open Percentage Modal
  const handleOpenPercentageModal = () => {
    setOpenPercentageModal(true);
  };

  // Close Percentage Modal
  const handleClosePercentageModal = () => {
    setOpenPercentageModal(false);
    setSearchANRPercentage("");
    setPercentageData([]);
    setTotalTime(0);
  };

  // Handle ANR input change in Percentage Modal
  const handleSearchANRPercentageChange = (event) => {
    setSearchANRPercentage(event.target.value);
  };

  // Handle Search in Percentage Modal
  const handleSearchPercentage = async () => {
    const trimmedANR = searchANRPercentage.trim();
    if (trimmedANR.length === 0) {
      setSnackbar({
        open: true,
        message: "Please enter an ANR to search.",
        severity: "warning",
      });
      return;
    }

    setLoadingPercentage(true);
    try {
      const response = await axios.get(
        `http://35.159.43.224:3080/m1/data/${trimmedANR}`,
      );
      console.log("API Response:", response.data);
      setPercentageData(response.data);

      // Calculate totalTime by summing up 'az' values
      const total = response.data.reduce(
        (acc, item) => acc + (item.az || 0),
        0,
      );
      setTotalTime(total);

      setLoadingPercentage(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch data for the provided ANR.",
        severity: "error",
      });
      setLoadingPercentage(false);
    }
  };

  // -----------------------------
  // Table Columns Definitions
  // -----------------------------

  // Columns for the main table
  const columns = useMemo(
    () => [
      {
        id: "icons",
        header: "Status",
        Cell: ({ row }) => {
          const hasUsers = row.original.totalUserCost > 0; // Adjust based on your data structure
          const hasMaterials =
            row.original.materials && row.original.materials.length > 0;

          return (
            <Box display='flex' alignItems='center' gap={1}>
              {hasUsers && <AccessTimeIcon color='primary' />}
              {hasMaterials && <CategoryIcon color='secondary' />}
            </Box>
          );
        },
        size: 100, // Adjust size as needed
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnOrdering: false,
      },
      {
        accessorKey: "ANR",
        header: "ANR",
        Cell: ({ row }) => (
          <span>
            {row.original.Modified_ANR
              ? row.original.Modified_ANR
              : row.original.ANR}
          </span>
        ),
      },
      {
        accessorKey: "Einzelpreis",
        header: "Einzelpreis",
        Cell: ({ row }) => (
          <span>
            {row.original.Modified_Einzelpreis
              ? row.original.Modified_Einzelpreis
              : row.original.Einzelpreis}
          </span>
        ),
      },
      {
        accessorKey: "Menge",
        header: "Menge",
        Cell: ({ row }) => (
          <span>
            {row.original.Modified_Menge !== undefined &&
            row.original.Modified_Menge !== null
              ? row.original.Modified_Menge
              : row.original.Menge}
          </span>
        ),
      },
      { accessorKey: "invoiceNo", header: "Invoice No" },
      {
        accessorKey: "totalPrice",
        header: "Gesamtpreis", // Updated header
        Cell: ({ cell }) => {
          const price = cell.getValue();
          if (price == null || isNaN(price)) {
            return "$0.00";
          }
          return `$${price.toFixed(2)}`;
        },
      },
      {
        accessorKey: "profitCost",
        header: "Profit Cost",
        Cell: ({ row }) => {
          const profit = row.original.profitCost;
          const isProfit = profit >= 0;
          const displayProfit = !isNaN(profit)
            ? `$${profit.toFixed(2)}`
            : "$0.00";
          return (
            <span style={{ color: isProfit ? "green" : "red" }}>
              {displayProfit}
            </span>
          );
        },
      },
      {
        accessorKey: "profitPercentage",
        header: "Profit Percentage",
        Cell: ({ row }) => {
          const percentage = row.original.profitPercentage;
          const isProfit = percentage >= 0;
          const displayPercentage = !isNaN(percentage)
            ? `${percentage.toFixed(2)}%`
            : "0.00%";
          return (
            <span style={{ color: isProfit ? "green" : "red" }}>
              {displayPercentage}
            </span>
          );
        },
      },
      // New Columns
      {
        accessorKey: "combinedTotalTime",
        header: "Combined Total Time",
        Cell: ({ row }) => {
          const modifiedTotalTime = row.original.Modified_TotalTime;
          const calculatedTime = row.original.combinedTotalTime;

          if (
            modifiedTotalTime !== undefined &&
            modifiedTotalTime !== null &&
            modifiedTotalTime !== 0
          ) {
            // Display Modified_TotalTime in a formatted manner (e.g., hours)
            return `${modifiedTotalTime} hrs`;
          } else if (calculatedTime) {
            // Existing calculation logic
            const timeStr = calculatedTime; // e.g., "4:26:1"

            if (!timeStr || typeof timeStr !== "string") {
              // Handle undefined or non-string values
              return "00:00:00";
            }

            const parts = timeStr.split(":").map((part) => {
              const num = parseInt(part, 10);
              return isNaN(num) ? "00" : num.toString().padStart(2, "0");
            });

            // Ensure we have exactly 3 parts (HH:MM:SS)
            while (parts.length < 3) {
              parts.push("00");
            }

            // Optionally, handle cases where there are more than 3 parts
            const [hours, minutes, seconds] = parts.slice(0, 3);

            return `${hours}:${minutes}:${seconds}`;
          } else {
            return "00:00:00";
          }
        },
      },
      {
        accessorKey: "totalUserCost",
        header: "Total User Cost",
        Cell: ({ cell }) => {
          const cost = cell.getValue();
          if (cost == null || isNaN(cost)) {
            return "$0.00";
          }
          return `$${cost.toFixed(2)}`;
        },
      },
      {
        accessorKey: "totalStuckCostSum",
        header: "Total Stuck Cost Sum",
        Cell: ({ cell }) => {
          const cost = cell.getValue();
          if (cost == null || isNaN(cost)) {
            return "$0.00";
          }
          return `$${cost.toFixed(2)}`;
        },
      },
      {
        accessorKey: "finalProductCost",
        header: "Final Product Cost",
        Cell: ({ cell }) => {
          const cost = cell.getValue();
          if (cost == null || isNaN(cost)) {
            return "$0.00";
          }
          return `$${cost.toFixed(2)}`;
        },
      },

      // Existing Action Columns
      {
        id: "addProjectTime",
        header: "Add Project Time",
        Cell: ({ row }) => (
          <Button
            variant='outlined'
            onClick={() =>
              handleOpenAddTimeModal(row.original.mainId, row.original.ANR)
            }>
            Add Time
          </Button>
        ),
      },
      {
        id: "addNewModel",
        header: "Add New Material",
        Cell: ({ row }) => (
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={() =>
              handleAddNewModelModalWithId(
                row.original.mainId,
                row.original.ANR,
              )
            }>
            Add Material
          </Button>
        ),
      },

      // New Edit Action Column
      {
        id: "edit",
        header: "Edit",
        Cell: ({ row }) => (
          <Button
            variant='contained'
            color='secondary'
            onClick={() => handleEditRow(row.original)}>
            Edit
          </Button>
        ),
      },

      // Actions Column
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant='outlined'
              color='info'
              onClick={() => handleViewTime(row.original.mainId)}>
              View Time
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => handleViewMaterials(row.original.materials)}>
              View Material
            </Button>
          </div>
        ),
      },
    ],
    [
      handleEditRow,
      handleOpenAddTimeModal,
      handleAddNewModelModalWithId,
      handleViewTime,
      handleViewMaterials,
    ],
  );

  // Columns for the "Add Time" modal's search results table
  const searchColumnsAddTime = useMemo(
    () => [
      { accessorKey: "ANR", header: "ANR" },
      { accessorKey: "projectName", header: "Project Name" },
      {
        accessorKey: "description",
        header: "Beschreibung",
        size: 150,
      },
      // Add other columns based on your project data structure
      { accessorKey: "userName", header: "User Name" }, // Adjust based on your time data structure
      { accessorKey: "date", header: "Date" }, // Adjust based on your time data structure
      { accessorKey: "ANR", header: "ANR" }, // Adjust based on your time data structure
      { accessorKey: "description", header: "Beschreibung" }, // Adjust based on your time data structure
      { accessorKey: "StartTime", header: "Startzeit" }, // Adjust based on your time data structure
      { accessorKey: "EndTime", header: "Endzeit" }, // Adjust based on your time data structure
      { accessorKey: "TotalTime", header: "Gesamtzeit" }, // Adjust based on your time data structure
      { accessorKey: "Menge", header: "Menge" }, // Adjust based on your time data structure
      { accessorKey: "Arbeiten", header: "Arbeiten" }, // Adjust based on your time data structure

      {
        id: "addUser",
        header: "Add",
        Cell: ({ row }) => (
          <Button
            variant='contained'
            color='primary'
            startIcon={<PersonAddIcon />}
            onClick={() => handleAddUser(row.original._id)} // Assuming row.original._id is ProjectUser's _id
          >
            Add User
          </Button>
        ),
      },
    ],
    [handleAddUser],
  );

  // Columns for the "Add New Material" modal's search results table
  const searchColumnsAddNewModel = useMemo(
    () => [
      { accessorKey: "rechnungId", header: "Rechnung ID" },
      { accessorKey: "ANR", header: "ANR" },
      { accessorKey: "position", header: "Position" },
      { accessorKey: "singleStuckCost", header: "Single Stuck Cost" },
      { accessorKey: "numberOfStuck", header: "Number of Stuck" },
      { accessorKey: "totalStuckCost", header: "Total Stuck Cost" },
      // Add other relevant fields
      {
        id: "addMaterial",
        header: "Add Material",
        Cell: ({ row }) => (
          <IconButton
            color='primary'
            onClick={() => handleAddMaterial(row.original)} // Pass the entire material object
          >
            <AddIcon />
          </IconButton>
        ),
      },
    ],
    [handleAddMaterial],
  );

  // Columns for the "View Materials" modal
  const materialColumns = useMemo(
    () => [
      { accessorKey: "ANR", header: "ANR" },
      { accessorKey: "rechnungId", header: "Rechnung ID" },
      { accessorKey: "singleStuckCost", header: "Single Stuck Cost" },
      { accessorKey: "numberOfStuck", header: "Number of Stuck" },
      { accessorKey: "totalStuckCost", header: "Total Stuck Cost" },
    ],
    [],
  );

  // Columns for the "View Time" modal's table
  const timeColumns = useMemo(
    () => [
      { accessorKey: "userName", header: "User Name" }, // Adjust based on your time data structure
      { accessorKey: "date", header: "Date" }, // Adjust based on your time data structure
      { accessorKey: "ANR", header: "ANR" }, // Adjust based on your time data structure
      { accessorKey: "description", header: "Beschreibung" }, // Adjust based on your time data structure
      { accessorKey: "StartTime", header: "Startzeit" }, // Adjust based on your time data structure
      { accessorKey: "EndTime", header: "Endzeit" }, // Adjust based on your time data structure
      { accessorKey: "TotalTime", header: "Gesamtzeit" }, // Adjust based on your time data structure
      { accessorKey: "Menge", header: "Menge" }, // Adjust based on your time data structure
      { accessorKey: "Arbeiten", header: "Arbeiten" }, // Adjust based on your time data structure
      // Add other relevant fields
    ],
    [],
  );

  // -----------------------------
  // New Columns for Percentage Modal's API Response Table
  // -----------------------------
  const percentageColumns = useMemo(
    () => [
      { accessorKey: "_id", header: "ID" },
      { accessorKey: "datum", header: "Datum" },
      { accessorKey: "Auftrag-Nr.", header: "Auftrag-Nr." },
      { accessorKey: "anr-un", header: "ANR-UN" },
      { accessorKey: "jahr", header: "Jahr" },
      { accessorKey: "az", header: "AZ" },
      { accessorKey: "artikel", header: "Artikel" },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "kunde", header: "Kunde" },
      { accessorKey: "laufzeit", header: "Laufzeit" },
      { accessorKey: "kosten", header: "Kosten" },
      { accessorKey: "Zulage 10%", header: "Zulage 10%" },
      { accessorKey: "Zulage 25%", header: "Zulage 25%" },
      { accessorKey: "Zulage 50%", header: "Zulage 50%" },
      { accessorKey: "Spalte1", header: "Spalte1" },
      { accessorKey: "Spalte2", header: "Spalte2" },
    ],
    [],
  );

  // -----------------------------
  // JSX Rendering
  // -----------------------------

  return (
    <div style={{ padding: "20px" }}>
      {/* Header Section */}
      <Typography variant='h4' gutterBottom>
        Final Calculations
      </Typography>

      {/* Buttons Section */}
      <Box mb={2} display='flex' gap={2}>
        {/* Calculate Button */}
        <Button
          variant='contained'
          color='success'
          onClick={handleOpenCalculationModal}
          disabled={
            Object.keys(selectedRowData).filter((id) => selectedRowData[id])
              .length === 0
          }>
          Calculate Profit/Loss of Selected
        </Button>

        {/* New Percentage Button */}
        <Button
          variant='outlined'
          color='primary'
          onClick={handleOpenPercentageModal}>
          Calculate Total Time by ANR
        </Button>
      </Box>

      {/* Main Table with Row Selection */}
      <MaterialReactTable
        columns={columns}
        data={data}
        getRowId={(row) => row.mainId} // Specify mainId as the unique row identifier
        initialState={{
          pagination: { pageSize: 10 }, // Adjust as needed
          sorting: [{ id: "ANR", desc: false }], // Example default sorting
        }}
        enableColumnOrdering
        enableSorting
        enablePagination
        enableRowSelection // Enable row selection
        onRowSelectionChange={setSelectedRowData} // Track selected rows
        state={{
          rowSelection: selectedRowData,
        }}
        // Add other props as needed
      />

      {/* Modal for "Add Time" */}
      <Modal open={openAddTime} onClose={handleCloseAddTimeModal}>
        <Box sx={modalStyle}>
          <Typography variant='h6' gutterBottom>
            Add Project Time
          </Typography>
          <TextField
            label='Enter ANR'
            fullWidth
            value={searchANRAddTime}
            onChange={handleSearchChangeAddTime}
            autoFocus
            margin='normal'
          />

          {/* Optionally, trigger search if ANR is pre-populated and meets length requirement */}
          {searchANRAddTime.length >= 3 && (
            <MaterialReactTable
              columns={searchColumnsAddTime}
              data={filteredProjects}
              enableFilters={false}
              enableSorting={false}
              enableGlobalFilter={false}
              enableColumnActions={false}
              enableColumnFilters={false}
              initialState={{
                pagination: { pageSize: 5 },
              }}
            />
          )}

          {/* Display messages based on search input */}
          {filteredProjects.length === 0 && searchANRAddTime.length >= 3 && (
            <Typography variant='body1' style={{ marginTop: "20px" }}>
              No results found for ANR: {searchANRAddTime}
            </Typography>
          )}
        </Box>
      </Modal>

      {/* Modal for "Add New Material" */}
      <Modal open={openAddNewModel} onClose={handleCloseAddNewModelModal}>
        <Box sx={modalStyle}>
          <Typography variant='h6' gutterBottom>
            Add Material
          </Typography>

          {/* Search Input for ANR */}
          <TextField
            label='Search ANR in Items'
            fullWidth
            value={searchTermAddNewModel}
            onChange={handleSearchChangeAddNewModel}
            margin='normal'
          />

          {/* Optionally, trigger search if ANR is pre-populated and meets length requirement */}
          {searchTermAddNewModel.length >= 3 &&
            (loadingAddNewModel ? (
              <Grid
                container
                justifyContent='center'
                style={{ marginTop: "20px" }}>
                <CircularProgress />
              </Grid>
            ) : searchResultsAddNewModel.length > 0 ? (
              <MaterialReactTable
                columns={searchColumnsAddNewModel}
                data={searchResultsAddNewModel}
                enableFilters={false}
                enableSorting={false}
                enableGlobalFilter={false}
                enableColumnActions={false}
                enableColumnFilters={false}
                initialState={{
                  pagination: { pageSize: 5 },
                }}
              />
            ) : (
              <Typography variant='body1' style={{ marginTop: "20px" }}>
                No items found matching the ANR: {searchTermAddNewModel}
              </Typography>
            ))}

          {/* Rest of your modal content */}
          {/* ... */}
        </Box>
      </Modal>

      {/* Modal for Viewing Materials */}
      <Modal open={openViewMaterials} onClose={handleCloseViewMaterials}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}>
          <Typography variant='h6' gutterBottom>
            Materials
          </Typography>
          {currentMaterials.length > 0 ? (
            <MaterialReactTable
              columns={materialColumns}
              data={currentMaterials}
              enableFilters={false}
              enableSorting={false}
              enableGlobalFilter={false}
              enableColumnActions={false}
              enableColumnFilters={false}
              initialState={{
                pagination: { pageSize: 5 },
              }}
            />
          ) : (
            <Typography variant='body1'>
              No materials added to this FinalCalculations entry.
            </Typography>
          )}
          <Box mt={2} textAlign='right'>
            <Button variant='contained' onClick={handleCloseViewMaterials}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal for Viewing Time */}
      <Modal open={openViewTime} onClose={handleCloseViewTime}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}>
          <Typography variant='h6' gutterBottom>
            Time Data
          </Typography>
          {/* Display Combined Time and Cost */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle1'>
              <strong>Combined Total Time:</strong>{" "}
              {data.find((item) => item.mainId === currentFinalCalcIdForTime)
                ?.combinedTotalTime || "N/A"}
            </Typography>
            <Typography variant='subtitle1'>
              <strong>Total User Cost:</strong> $
              {data
                .find((item) => item.mainId === currentFinalCalcIdForTime)
                ?.totalUserCost?.toFixed(2) || "N/A"}
            </Typography>
            <Typography variant='subtitle1'>
              <strong>Total Stuck Cost Sum:</strong> $
              {data
                .find((item) => item.mainId === currentFinalCalcIdForTime)
                ?.totalStuckCostSum?.toFixed(2) || "N/A"}
            </Typography>
            <Typography variant='subtitle1'>
              <strong>Final Product Cost:</strong> $
              {data
                .find((item) => item.mainId === currentFinalCalcIdForTime)
                ?.finalProductCost?.toFixed(2) || "N/A"}
            </Typography>
          </Box>
          {currentTimeData.length > 0 ? (
            <MaterialReactTable
              columns={timeColumns}
              data={currentTimeData}
              enableFilters={false}
              enableSorting={false}
              enableGlobalFilter={false}
              enableColumnActions={false}
              enableColumnFilters={false}
              initialState={{
                pagination: { pageSize: 5 },
              }}
            />
          ) : (
            <Typography variant='body1'>
              No time data available for this entry.
            </Typography>
          )}
          <Box mt={2} textAlign='right'>
            <Button variant='contained' onClick={handleCloseViewTime}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal for Edit */}
      <Modal open={openEditModal} onClose={handleCloseEditModal}>
        <Box sx={editModalStyle}>
          <Typography variant='h6' gutterBottom>
            Edit FinalCalculations
          </Typography>
          <TextField
            label='Modified ANR'
            name='Modified_ANR'
            fullWidth
            value={editData.Modified_ANR}
            onChange={handleEditInputChange}
            margin='normal'
          />
          <TextField
            label='Modified Menge'
            name='Modified_Menge'
            type='number'
            fullWidth
            value={editData.Modified_Menge}
            onChange={handleEditInputChange}
            margin='normal'
            inputProps={{ min: 0 }}
          />
          <TextField
            label='Modified Einzelpreis'
            name='Modified_Einzelpreis'
            fullWidth
            value={editData.Modified_Einzelpreis}
            onChange={handleEditInputChange}
            margin='normal'
          />
          <TextField
            label='Modified Total Time'
            name='Modified_TotalTime'
            type='number'
            fullWidth
            value={editData.Modified_TotalTime}
            onChange={handleEditInputChange}
            margin='normal'
            inputProps={{ min: 0 }}
            helperText='Enter total time in hours (e.g., 4.5)'
          />
          <Box mt={2} textAlign='right'>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmitEdit}
              style={{ marginRight: "10px" }}>
              Save
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              onClick={handleCloseEditModal}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal for Calculation Results */}
      <Modal open={openCalculationModal} onClose={handleCloseCalculationModal}>
        <Box sx={calculationModalStyle}>
          <Typography variant='h6' gutterBottom>
            Calculation Result
          </Typography>
          {calculationResult && (
            <Box>
              <Typography variant='subtitle1'>
                <strong>Combined Profit/Loss:</strong>
                <span
                  style={{
                    color:
                      calculationResult.combinedProfitCost >= 0
                        ? "green"
                        : "red",
                    marginLeft: "10px",
                  }}>
                  ${calculationResult.combinedProfitCost.toFixed(2)}
                </span>
              </Typography>
              <Typography variant='subtitle1'>
                <strong>Profit/Loss Percentage:</strong>
                <span
                  style={{
                    color:
                      calculationResult.combinedProfitPercentage >= 0
                        ? "green"
                        : "red",
                    marginLeft: "10px",
                  }}>
                  {calculationResult.combinedProfitPercentage.toFixed(2)}%
                </span>
              </Typography>
            </Box>
          )}
          <Box mt={2} textAlign='right'>
            <Button variant='contained' onClick={handleCloseCalculationModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* New Modal for Percentage Calculation */}
      <Modal open={openPercentageModal} onClose={handleClosePercentageModal}>
        <Box sx={modalStyle}>
          <Typography variant='h6' gutterBottom>
            Calculate Total Time by ANR
          </Typography>

          {/* ANR Input and Search Button */}
          <Box display='flex' gap={2} alignItems='center' mb={2}>
            <TextField
              label='Enter ANR'
              value={searchANRPercentage}
              onChange={handleSearchANRPercentageChange}
              fullWidth
            />
            <Button
              variant='contained'
              color='primary'
              onClick={handleSearchPercentage}
              disabled={loadingPercentage}>
              {loadingPercentage ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Box>

          {/* Display totalTime */}
          {percentageData.length > 0 && (
            <Typography variant='subtitle1' gutterBottom>
              <strong>Total Time (az):</strong> {totalTime}
            </Typography>
          )}

          {/* Display API Response in Table */}
          {percentageData.length > 0 ? (
            <MaterialReactTable
              columns={percentageColumns}
              data={percentageData}
              enableFilters={false}
              enableSorting={false}
              enableGlobalFilter={false}
              enableColumnActions={false}
              enableColumnFilters={false}
              initialState={{
                pagination: { pageSize: 5 },
              }}
            />
          ) : (
            !loadingPercentage && (
              <Typography variant='body1'>
                No data to display. Please perform a search.
              </Typography>
            )
          )}

          {/* Handle Error Messages */}
          {percentageData.length === 0 &&
            !loadingPercentage &&
            searchANRPercentage.trim().length >= 3 && (
              <Typography variant='body1' style={{ marginTop: "20px" }}>
                No results found for ANR: {searchANRPercentage}
              </Typography>
            )}
        </Box>
      </Modal>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

// Modal styling for "Add Time", "Add New Material", etc.
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  width: "90%",
  maxWidth: 1200,
  maxHeight: "90vh",
  overflowY: "auto",
};

// Modal styling for Edit Modal
const editModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  width: 400,
  borderRadius: "8px",
};

// Modal styling for Calculation Results
const calculationModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  width: 400,
  borderRadius: "8px",
};

export default FinalCalculations;
